
import type { LucideIcon } from 'lucide-react';
import { ClipboardCheck, Crown, PenLine, Shield, Sparkles, Star } from 'lucide-react';
import { DiscoverDialogClose } from '@/components/help-center/DiscoverDialogClose';
import type { PainelRole } from '@/lib/painel-rbac';
import { PAINEL_ROLE_LABELS } from '@/lib/painel-rbac';
import { painelPermissionsModalShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';

export type RoleVisualConfig = {
  icon: LucideIcon;
  iconShell: string;
  iconColor: string;
  glow: string;
};

export const BUILTIN_ROLE_VISUALS: Record<PainelRole, RoleVisualConfig> = {
  super_admin: {
    icon: Crown,
    iconShell: 'bg-gradient-to-br from-amber-500/25 to-amber-700/10 ring-amber-400/35',
    iconColor: 'text-amber-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(251,191,36,0.35)]',
  },
  admin: {
    icon: Star,
    iconShell: 'bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-sky-400/35',
    iconColor: 'text-sky-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]',
  },
  editor: {
    icon: PenLine,
    iconShell: 'bg-gradient-to-br from-violet-500/22 to-violet-700/10 ring-violet-400/30',
    iconColor: 'text-violet-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(167,139,250,0.3)]',
  },
  reviewer: {
    icon: ClipboardCheck,
    iconShell: 'bg-gradient-to-br from-cyan-500/22 to-cyan-700/10 ring-cyan-400/30',
    iconColor: 'text-cyan-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(34,211,238,0.28)]',
  },
};

export const CUSTOM_ROLE_VISUAL: RoleVisualConfig = {
  icon: Sparkles,
  iconShell: 'bg-gradient-to-br from-indigo-500/22 to-indigo-700/10 ring-indigo-400/30',
  iconColor: 'text-indigo-200',
  glow: 'shadow-[0_0_40px_-10px_rgba(129,140,248,0.28)]',
};

export const ROLE_BADGE_STYLES: Record<
  PainelRole,
  { shell: string; text: string; icon: string }
> = {
  super_admin: {
    shell:
      'bg-gradient-to-r from-amber-500/25 via-amber-400/15 to-yellow-600/10 ring-amber-500/70 shadow-[0_0_18px_-4px_rgba(251,191,36,0.4)]',
    text: 'text-amber-50',
    icon: 'text-amber-300',
  },
  admin: {
    shell:
      'bg-gradient-to-r from-sky-500/25 via-sky-400/15 to-indigo-500/10 ring-sky-400/45 shadow-[0_0_18px_-4px_rgba(56,189,248,0.35)]',
    text: 'text-sky-50',
    icon: 'text-sky-300',
  },
  editor: {
    shell:
      'bg-gradient-to-r from-violet-500/25 via-violet-400/15 to-purple-600/10 ring-violet-400/40 shadow-[0_0_18px_-4px_rgba(167,139,250,0.32)]',
    text: 'text-violet-50',
    icon: 'text-violet-300',
  },
  reviewer: {
    shell:
      'bg-gradient-to-r from-cyan-500/25 via-teal-400/15 to-cyan-600/10 ring-cyan-400/40 shadow-[0_0_18px_-4px_rgba(34,211,238,0.3)]',
    text: 'text-cyan-50',
    icon: 'text-cyan-300',
  },
};

type PainelRoleBadgeProps = {
  role: PainelRole;
  compact?: boolean;
  className?: string;
};

export function PainelRoleBadge({ role, compact, className }: PainelRoleBadgeProps) {
  const visual = BUILTIN_ROLE_VISUALS[role];
  const badge = ROLE_BADGE_STYLES[role];
  const Icon = visual.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-inter font-semibold ring-1',
        badge.shell,
        badge.text,
        compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]',
        className
      )}
    >
      <Icon className={cn(compact ? 'h-3 w-3' : 'h-3.5 w-3.5', badge.icon)} aria-hidden />
      {PAINEL_ROLE_LABELS[role]}
    </span>
  );
}

type PainelRoleIconProps = {
  config: RoleVisualConfig;
  size?: 'md' | 'lg';
  className?: string;
};

export function PainelRoleIcon({ config, size = 'md', className }: PainelRoleIconProps) {
  const Icon = config.icon;
  const box = size === 'lg' ? 'h-14 w-14 rounded-2xl' : 'h-12 w-12 rounded-2xl';
  const iconSize = size === 'lg' ? 'h-7 w-7' : 'h-6 w-6';

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center ring-1',
        box,
        config.iconShell,
        config.glow,
        className
      )}
    >
      <Icon className={cn(iconSize, config.iconColor)} aria-hidden />
    </span>
  );
}

type PermissionCountBadgeProps = {
  active: number;
  total: number;
};

export function PermissionCountBadge({ active, total }: PermissionCountBadgeProps) {
  const pct = total > 0 ? Math.round((active / total) * 100) : 0;

  return (
    <div className="flex items-center gap-3">
      <div className="relative h-11 w-11 shrink-0">
        <svg className="h-11 w-11 -rotate-90" viewBox="0 0 36 36" aria-hidden>
          <circle
            cx="18"
            cy="18"
            r="15.5"
            fill="none"
            className="stroke-white/10"
            strokeWidth="3"
          />
          <circle
            cx="18"
            cy="18"
            r="15.5"
            fill="none"
            className="stroke-sky-400"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${pct} 100`}
            pathLength={100}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-poppins text-[11px] font-bold text-sky-200">
          {pct}%
        </span>
      </div>
      <div className="min-w-0">
        <p className="font-poppins text-sm font-semibold text-white">
          {active}
          <span className="font-normal text-slate-500"> / {total}</span>
        </p>
        <p className="font-inter text-[11px] text-slate-500">permissões ativas</p>
      </div>
    </div>
  );
}

export function PainelPermissionsDialogShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative flex min-h-0 flex-col', painelPermissionsModalShellClass, className)}>
      <DiscoverDialogClose className="right-5 top-5" />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#030023]/40 via-[#000b6a]/20 to-[#030023]/50"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-36 bg-[radial-gradient(ellipse_80%_55%_at_50%_-20%,rgba(56,189,248,0.12),transparent)]"
        aria-hidden
      />
      <div className="relative z-10 flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  );
}
